import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Calendar,
  ChevronLeft,
  Clock,
  FileText,
  Loader2,
  Plus,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API, formatNumber, getPlatformName } from "../components/dashboard/constants";

const T = {
  ko: {
    title: "스필오버 리포트",
    description: "자동 생성된 주간/월간 스필오버 분석 리포트를 확인합니다.",
    weekly: "주간",
    monthly: "월간",
    allTypes: "전체",
    selectGroup: "그룹 선택",
    generate: "지금 생성",
    generating: "생성 중...",
    noReports: "아직 생성된 리포트가 없습니다.",
    noReportsDesc: "'지금 생성' 버튼을 클릭하거나 자동 스케줄을 통해 리포트가 생성됩니다.",
    back: "목록으로",
    period: "분석 기간",
    prevPeriod: "비교 기간",
    generatedAt: "생성 시각",
    patterns: "탐지 패턴",
    events: "이벤트",
    posRatio: "긍정 비율",
    lift: "총 Lift",
    source: "최강 소스",
    target: "최강 타깃",
    generateSuccess: "리포트가 생성되었습니다",
    generateFailed: "리포트 생성에 실패했습니다",
    fetchFailed: "리포트 목록을 불러오지 못했습니다",
    change: "변화",
    newPairs: "신규 페어",
    gonePairs: "소멸 페어",
  },
  en: {
    title: "Spillover Reports",
    description: "View auto-generated weekly/monthly spillover analysis reports.",
    weekly: "Weekly",
    monthly: "Monthly",
    allTypes: "All",
    selectGroup: "Select group",
    generate: "Generate Now",
    generating: "Generating...",
    noReports: "No reports generated yet.",
    noReportsDesc: "Click 'Generate Now' or wait for the automated schedule.",
    back: "Back to list",
    period: "Analysis Period",
    prevPeriod: "Comparison Period",
    generatedAt: "Generated",
    patterns: "Patterns",
    events: "Events",
    posRatio: "Positive %",
    lift: "Total Lift",
    source: "Strongest Source",
    target: "Strongest Target",
    generateSuccess: "Report generated successfully",
    generateFailed: "Report generation failed",
    fetchFailed: "Failed to load reports",
    change: "Change",
    newPairs: "New Pairs",
    gonePairs: "Gone Pairs",
  },
  zh: {
    title: "溢出报告",
    description: "查看自动生成的周报/月报溢出分析报告。",
    weekly: "周报",
    monthly: "月报",
    allTypes: "全部",
    selectGroup: "选择分组",
    generate: "立即生成",
    generating: "生成中...",
    noReports: "暂无生成的报告。",
    noReportsDesc: "点击「立即生成」或等待自动计划任务。",
    back: "返回列表",
    period: "分析期间",
    prevPeriod: "对比期间",
    generatedAt: "生成时间",
    patterns: "模式",
    events: "事件",
    posRatio: "正面比例",
    lift: "总 Lift",
    source: "最强来源",
    target: "最强目标",
    generateSuccess: "报告生成成功",
    generateFailed: "报告生成失败",
    fetchFailed: "加载报告列表失败",
    change: "变化",
    newPairs: "新增配对",
    gonePairs: "消失配对",
  },
};

const parseMarkdownBlocks = (markdown) => {
  const blocks = [];
  let listItems = [];
  let ordered = false;
  const flushList = () => { if (!listItems.length) return; blocks.push({ type: ordered ? "ol" : "ul", items: [...listItems] }); listItems = []; };
  (markdown || "").split("\n").forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) { flushList(); blocks.push({ type: "space" }); return; }
    const orderedMatch = trimmed.match(/^\d+\.\s+(.*)$/);
    if (orderedMatch) { if (listItems.length && !ordered) flushList(); ordered = true; listItems.push(orderedMatch[1]); return; }
    if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) { if (listItems.length && ordered) flushList(); ordered = false; listItems.push(trimmed.slice(2)); return; }
    flushList(); ordered = false;
    if (trimmed.startsWith("### ")) { blocks.push({ type: "h3", text: trimmed.slice(4) }); return; }
    if (trimmed.startsWith("## ")) { blocks.push({ type: "h2", text: trimmed.slice(3) }); return; }
    if (trimmed.startsWith("# ")) { blocks.push({ type: "h1", text: trimmed.slice(2) }); return; }
    blocks.push({ type: "p", text: trimmed });
  });
  flushList();
  return blocks;
};

const renderInlineMarkdown = (text) => {
  if (!text) return text;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const MarkdownReport = ({ markdown }) => {
  const blocks = useMemo(() => parseMarkdownBlocks(markdown), [markdown]);
  return (
    <div className="space-y-3" data-testid="report-markdown-body">
      {blocks.map((block, i) => {
        if (block.type === "space") return <div key={`s-${i}`} className="h-1" />;
        if (block.type === "h1") return <h2 key={`h1-${i}`} className="text-2xl font-semibold tracking-tight text-foreground">{renderInlineMarkdown(block.text)}</h2>;
        if (block.type === "h2") return <h3 key={`h2-${i}`} className="pt-2 text-lg font-semibold text-foreground">{renderInlineMarkdown(block.text)}</h3>;
        if (block.type === "h3") return <h4 key={`h3-${i}`} className="text-sm font-semibold uppercase tracking-[0.18em] text-muted-foreground">{renderInlineMarkdown(block.text)}</h4>;
        if (block.type === "ol") return <ol key={`ol-${i}`} className="space-y-2 pl-5 text-sm leading-7 text-muted-foreground list-decimal">{block.items.map((item, j) => <li key={`ol-${j}`}>{renderInlineMarkdown(item)}</li>)}</ol>;
        if (block.type === "ul") return <ul key={`ul-${i}`} className="space-y-2 pl-5 text-sm leading-7 text-muted-foreground list-disc">{block.items.map((item, j) => <li key={`ul-${j}`}>{renderInlineMarkdown(item)}</li>)}</ul>;
        return <p key={`p-${i}`} className="text-sm leading-7 text-muted-foreground">{renderInlineMarkdown(block.text)}</p>;
      })}
    </div>
  );
};

const ReportListItem = ({ report, lang, t, onClick }) => {
  const ctx = report.report_context || {};
  const curr = ctx.current || {};
  return (
    <button
      type="button"
      onClick={() => onClick(report.report_id)}
      className="w-full rounded-2xl border border-border/60 bg-background/70 p-4 text-left transition-colors hover:border-primary/30 hover:bg-muted/20"
      data-testid={`report-item-${report.report_id}`}
    >
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Badge variant="outline" className={report.report_type === "weekly" ? "border-blue-500/30 bg-blue-500/10 text-blue-600 text-[10px]" : "border-violet-500/30 bg-violet-500/10 text-violet-600 text-[10px]"}>
          {report.report_type === "weekly" ? t.weekly : t.monthly}
        </Badge>
        <span className="text-sm font-medium text-foreground">{report.group_name}</span>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span><Calendar className="inline mr-1 h-3 w-3" />{report.period_start} ~ {report.period_end}</span>
        <span>{t.events}: {curr.events || 0}</span>
        <span>{t.posRatio}: {curr.positive_ratio || 0}%</span>
        <span>{t.lift}: {formatNumber(curr.total_lift || 0)}</span>
        {report.patterns?.length ? <span>{t.patterns}: {report.patterns.length}</span> : null}
      </div>
      <p className="mt-1.5 text-[10px] text-muted-foreground/50">
        <Clock className="inline mr-1 h-3 w-3" />
        {t.generatedAt}: {new Date(report.generated_at).toLocaleString(lang === "ko" ? "ko-KR" : lang === "zh" ? "zh-CN" : "en-US")}
      </p>
    </button>
  );
};

const ReportDetail = ({ report, lang, t, onBack }) => {
  const ctx = report.report_context || {};
  const curr = ctx.current || {};
  const prev = ctx.previous || {};
  const changes = ctx.changes || {};

  return (
    <div className="space-y-4" data-testid="report-detail-view">
      <Button type="button" variant="ghost" size="sm" onClick={onBack} data-testid="report-back-button">
        <ChevronLeft className="mr-1 h-4 w-4" />
        {t.back}
      </Button>

      <Card className="border-primary/20 bg-gradient-to-b from-primary/4 via-background to-background">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className={report.report_type === "weekly" ? "border-blue-500/30 bg-blue-500/10 text-blue-600" : "border-violet-500/30 bg-violet-500/10 text-violet-600"}>
              {report.report_type === "weekly" ? t.weekly : t.monthly}
            </Badge>
            <Badge variant="outline" className="border-border/60 bg-background/70">
              <Sparkles className="mr-1 h-3 w-3" />
              GPT-5.2
            </Badge>
          </div>
          <CardTitle className="text-base font-semibold">{report.group_name}</CardTitle>
          <CardDescription>{t.period}: {report.period_start} ~ {report.period_end}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4 mb-4">
            <div className="rounded-xl border border-border/60 bg-background/70 p-2.5">
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground/60">{t.events}</p>
              <p className="text-lg font-semibold text-foreground">{curr.events || 0}</p>
              <p className="text-[10px] text-muted-foreground">prev: {prev.events || 0} ({changes.events_delta > 0 ? "+" : ""}{changes.events_delta || 0})</p>
            </div>
            <div className="rounded-xl border border-border/60 bg-background/70 p-2.5">
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground/60">{t.posRatio}</p>
              <p className="text-lg font-semibold text-foreground">{curr.positive_ratio || 0}%</p>
              <p className="text-[10px] text-muted-foreground">prev: {prev.positive_ratio || 0}% ({changes.ratio_delta > 0 ? "+" : ""}{changes.ratio_delta || 0}p)</p>
            </div>
            <div className="rounded-xl border border-border/60 bg-background/70 p-2.5">
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground/60">{t.lift}</p>
              <p className="text-lg font-semibold text-foreground">{formatNumber(curr.total_lift || 0)}</p>
              <p className="text-[10px] text-muted-foreground">prev: {formatNumber(prev.total_lift || 0)}</p>
            </div>
            <div className="rounded-xl border border-border/60 bg-background/70 p-2.5">
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground/60">{t.source} / {t.target}</p>
              <p className="text-sm font-semibold text-foreground">{curr.strongest_source ? getPlatformName(curr.strongest_source, lang) : "-"}</p>
              <p className="text-xs text-muted-foreground">{curr.strongest_target ? getPlatformName(curr.strongest_target, lang) : "-"}</p>
            </div>
          </div>

          {changes.new_pairs?.length || changes.gone_pairs?.length ? (
            <div className="flex flex-wrap gap-2 mb-4 text-xs">
              {(changes.new_pairs || []).map((p) => (
                <Badge key={`new-${p}`} variant="outline" className="border-blue-500/30 bg-blue-500/10 text-blue-600 text-[10px]">NEW {p}</Badge>
              ))}
              {(changes.gone_pairs || []).map((p) => (
                <Badge key={`gone-${p}`} variant="outline" className="border-zinc-500/30 bg-zinc-500/10 text-zinc-500 text-[10px]">GONE {p}</Badge>
              ))}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card className="border-border/60" data-testid="report-markdown-card">
        <CardContent className="p-5 md:p-6">
          <MarkdownReport markdown={report.report_markdown} />
        </CardContent>
      </Card>
    </div>
  );
};

export default function ReportsPage({ lang = "ko" }) {
  const t = T[lang] || T.ko;
  const { token } = useAuth();
  const headers = useMemo(() => token ? { Authorization: `Bearer ${token}` } : {}, [token]);

  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [reportType, setReportType] = useState("all");
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);

  const fetchGroups = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/artist-groups`, { headers });
      const list = response.data?.groups || response.data || [];
      setGroups(list);
      if (list.length && !selectedGroup) setSelectedGroup(list[0].id);
    } catch { /* ignore */ }
  }, [headers, selectedGroup]);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedGroup) params.group_id = selectedGroup;
      if (reportType !== "all") params.report_type = reportType;
      const response = await axios.get(`${API}/spillover/reports`, { headers, params });
      setReports(response.data?.reports || []);
    } catch {
      toast.error(t.fetchFailed);
    } finally {
      setLoading(false);
    }
  }, [headers, reportType, selectedGroup, t.fetchFailed]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);
  useEffect(() => { if (token) fetchReports(); }, [fetchReports, token]);

  const generateReport = async (type) => {
    if (!selectedGroup) return;
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/spillover/reports/generate`, null, {
        headers,
        params: { group_id: selectedGroup, report_type: type, language: lang },
      });
      toast.success(t.generateSuccess);
      setReports((prev) => [response.data, ...prev]);
      setSelectedReportId(response.data.report_id);
    } catch {
      toast.error(t.generateFailed);
    } finally {
      setGenerating(false);
    }
  };

  const selectedReport = reports.find((r) => r.report_id === selectedReportId);
  const selectedGroupName = groups.find((g) => g.id === selectedGroup)?.name || "";

  if (selectedReport) {
    return (
      <div className="mx-auto w-full max-w-5xl space-y-4 p-4 md:p-6" data-testid="reports-page">
        <ReportDetail report={selectedReport} lang={lang} t={t} onBack={() => setSelectedReportId(null)} />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-4 p-4 md:p-6" data-testid="reports-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-foreground" data-testid="reports-title">{t.title}</h1>
          <p className="text-sm text-muted-foreground">{t.description}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" variant="outline" onClick={() => generateReport("weekly")} disabled={!selectedGroup || generating} data-testid="generate-weekly-button">
            {generating ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-1 h-3.5 w-3.5" />}
            {generating ? t.generating : `${t.weekly} ${t.generate}`}
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => generateReport("monthly")} disabled={!selectedGroup || generating} data-testid="generate-monthly-button">
            {generating ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-1 h-3.5 w-3.5" />}
            {generating ? t.generating : `${t.monthly} ${t.generate}`}
          </Button>
        </div>
      </div>

      <Card className="border-border/60">
        <CardContent className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center">
          <Select value={selectedGroup} onValueChange={setSelectedGroup}>
            <SelectTrigger data-testid="reports-group-selector" className="w-full sm:w-[220px]">
              <SelectValue placeholder={t.selectGroup} />
            </SelectTrigger>
            <SelectContent>
              {groups.map((g) => (
                <SelectItem key={g.id} value={g.id}>{g.name} ({g.account_count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={reportType} onValueChange={setReportType}>
            <SelectTrigger data-testid="reports-type-selector" className="w-full sm:w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.allTypes}</SelectItem>
              <SelectItem value="weekly">{t.weekly}</SelectItem>
              <SelectItem value="monthly">{t.monthly}</SelectItem>
            </SelectContent>
          </Select>
          <Button type="button" size="sm" variant="ghost" onClick={fetchReports} disabled={loading} data-testid="reports-refresh-button">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </CardContent>
      </Card>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-28 w-full rounded-2xl" />)}
        </div>
      ) : !reports.length ? (
        <Card data-testid="reports-empty-state">
          <CardContent className="py-12 text-center">
            <FileText className="mx-auto mb-3 h-10 w-10 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">{t.noReports}</p>
            <p className="mt-1 text-xs text-muted-foreground/60">{t.noReportsDesc}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="reports-list">
          {reports.map((report) => (
            <ReportListItem key={report.report_id} report={report} lang={lang} t={t} onClick={setSelectedReportId} />
          ))}
        </div>
      )}
    </div>
  );
}
